import React from "react";
import { Box } from "@mui/material";
import RedoIcon from "@mui/icons-material/Redo";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import colors from "../../../Constants/colors";

interface DraggableAnnexItemProps {
  doc: { id?: number };
  children: React.ReactNode;
  showSubsectionHint?: boolean;
  isSubsection?: boolean;
}

export const DraggableAnnexItem: React.FC<DraggableAnnexItemProps> = ({
  doc,
  children,
  showSubsectionHint = false,
  isSubsection = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `annex-${doc.id}`,
    data: { doc },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${doc.id}-annex`,
    data: { id: doc.id },
    disabled: isDragging,
  });

  const { setNodeRef: setRightDropRef } = useDroppable({
    id: `${doc.id}-subsection`,
    data: { id: doc.id },
    disabled: isDragging || isSubsection,
  });

  const isHovered = isOver && !isDragging;

  return (
    <Box
      ref={setDragRef}
      {...attributes}
      {...listeners}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab",
        borderRadius: 1,
        transition: "opacity 0.2s",
        position: "relative",
        pb: 0.5,
      }}
    >
      <Box
        sx={{
          display: isHovered ? "block" : "none",
          height: 56,
          opacity: 0.2,
          backgroundColor: colors.accent,
          zIndex: 10,
          borderRadius: 1,
        }}
      />
      <Box
        sx={{
          pt: isHovered ? 0.5 : 0,
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        {children}
      </Box>
      <Box
        ref={setDropRef}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
      {!isSubsection && (
        <Box
          ref={setRightDropRef}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "30%",
            height: 60,
            zIndex: 1,
            pointerEvents: "none",
            backgroundColor:
              showSubsectionHint && !isDragging
                ? colors.orange50
                : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showSubsectionHint && !isDragging && (
            <RedoIcon fontSize="small" sx={{ color: colors.green700 }} />
          )}
        </Box>
      )}
    </Box>
  );
};
